import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { initials } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Users, AlertTriangle, Wrench, Truck, Settings as SettingsIcon,
  HardHat, LogOut, Menu, X, ShieldCheck, Eye, History, GraduationCap, Plane,
} from "lucide-react";
import InstallPrompt from "@/components/InstallPrompt";

const NAV = [
  { label: "Panel Principal", path: "/dashboard", icon: LayoutDashboard },
  { label: "Empleados", path: "/empleados", icon: Users },
  { label: "Alumnos en Formación", path: "/alumnos", icon: GraduationCap },
  { label: "Avisos y Caducidades", path: "/caducidades", icon: AlertTriangle },
  { label: "Herramientas", path: "/herramientas", icon: Wrench },
  { label: "Flota de Vehículos", path: "/vehiculos", icon: Truck },
  { label: "Vacaciones", path: "/vacaciones", icon: Plane },
  { label: "Histórico de Cambios", path: "/historial", icon: History },
  { label: "Ajustes y Copias", path: "/ajustes", icon: SettingsIcon },
];

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const [settings, setSettings] = useState(null);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [location.pathname]);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const Sidebar = (
    <aside className="w-64 shrink-0 bg-primary text-primary-foreground flex flex-col h-full">
      <div className="h-16 flex items-center gap-3 px-5 border-b border-white/10">
        {settings?.logo ? (
          <img src={settings.logo} alt="logo" className="h-9 w-9 rounded object-contain bg-white" />
        ) : (
          <div className="h-9 w-9 rounded bg-accent flex items-center justify-center">
            <HardHat className="h-5 w-5 text-white" />
          </div>
        )}
        <div className="min-w-0">
          <p className="font-display font-bold text-sm leading-tight truncate">
            {settings?.company_name || "Control de Personal"}
          </p>
          <p className="text-[11px] text-slate-400">Gestión de Plantilla</p>
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {NAV.map((item) => (
          <NavLink key={item.path} to={item.path} data-testid={`nav-${item.path.slice(1)}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-accent text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"
              }`
            }>
            <item.icon className="h-[18px] w-[18px]" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10">
        <div className="flex items-center gap-3 px-2 py-2 mb-2">
          <div className="h-9 w-9 rounded-full bg-accent/20 border border-accent/40 flex items-center justify-center text-xs font-semibold">
            {initials(user?.name || user?.email)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{user?.name || user?.email}</p>
            <span className={`inline-flex items-center gap-1 text-[11px] ${isAdmin ? "text-accent" : "text-slate-400"}`}>
              {isAdmin ? <ShieldCheck className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
              {isAdmin ? "Administrador" : user?.role === "trabajador" ? "Trabajador" : "Usuario · solo lectura"}
            </span>
          </div>
        </div>
        <Button variant="ghost" className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/5"
          onClick={logout} data-testid="logout-button">
          <LogOut className="h-4 w-4 mr-2" /> Cerrar sesión
        </Button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex bg-background">
      <div className="hidden lg:block sticky top-0 h-screen">{Sidebar}</div>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{Sidebar}</div>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden h-16 flex items-center justify-between px-4 border-b bg-white sticky top-0 z-40">
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-button">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <span className="font-display font-bold">{settings?.company_name || "Control de Personal"}</span>
          <div className="w-6" />
        </header>
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
      <InstallPrompt />
    </div>
  );
}
